import React, { useEffect, useState } from "react";
import {
  Check,
  AlertCircle,
  Loader2,
  TrendingUp,
  Package,
  DollarSign,
  ShoppingBag,
  RefreshCw,
} from "lucide-react";
import axiosInstance from "../../../utils/axiosInstance";
import { message, Modal } from "antd";
import { getTenantId } from "../../../utils/auth";
import {
  SHOPIFY_INTEGRATION_AUTH_API,
} from "../../../constants/API";
import DPAModal from "../../../components/DPAModal";
import SyncStatusModal from "../../../components/SyncStatusModal";

const INTEGRATIONS_STATUS_API = "/api/integrations/status";
const SYNC_STATUS_API = "/api/integrations/sync-status";
const TRIGGER_SYNC_API = "/api/integrations/trigger-sync";

interface IntegrationStatus {
  amazon: "idle" | "loading" | "connected" | "error";
  shopify: "idle" | "loading" | "connected" | "error";
  woocommerce: "idle" | "loading" | "connected" | "error";
  etsy: "idle" | "loading" | "connected" | "error";
}

interface SalesData {
  platform: string;
  revenue: string;
  orders: number;
  growth: string;
}

const EcommerceIntegration: React.FC = () => {
  const [status, setStatus] = useState<IntegrationStatus>({
    amazon: "idle",
    shopify: "idle",
    woocommerce: "idle",
    etsy: "idle",
  });

  const [salesData, setSalesData] = useState<SalesData[]>([]);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);
  const [syncHistory, setSyncHistory] = useState<any[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showDPAModal, setShowDPAModal] = useState(false);
  const [dpaAccepted, setDpaAccepted] = useState(false);

  // Platform-specific sync modal state
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [platformSyncHistory, setPlatformSyncHistory] = useState<any[]>([]);

  // Fetch integration status from API
  const fetchIntegrationStatus = async () => {
    try {
      const response = await axiosInstance.get(INTEGRATIONS_STATUS_API);

      if (response.data.status === "success" && response.data.integrations) {
        const integrations = response.data.integrations;

        // Map backend status to frontend status
        const mapStatus = (backendStatus: string) => {
          if (backendStatus === "active" || backendStatus === "ACTIVE") return "connected";
          if (backendStatus === "not_connected") return "idle";
          if (backendStatus === "error" || backendStatus === "ERROR") return "error";
          return "idle";
        };

        setStatus({
          amazon: integrations.amazon ? mapStatus(integrations.amazon.status) : "idle",
          shopify: integrations.shopify ? mapStatus(integrations.shopify.status) : "idle",
          woocommerce: integrations.woocommerce ? mapStatus(integrations.woocommerce.status) : "idle",
          etsy: integrations.etsy ? mapStatus(integrations.etsy.status) : "idle",
        });

        console.log("[Integration Status] Fetched from API:", integrations);
      }
    } catch (error) {
      console.error("[Integration Status] Error fetching status:", error);
    } finally {
      setIsLoadingStatus(false);
    }
  };

  // Fetch status on component mount
  useEffect(() => {
    fetchIntegrationStatus();
  }, []);

  // Fetch sync history from API
  const fetchSyncHistory = async () => {
    try {
      const response = await axiosInstance.get(SYNC_STATUS_API);
      if (response.data.status === "success") {
        setSyncHistory(response.data.sync_history || []);
      }
    } catch (error) {
      console.error("[Sync Status] Error fetching sync history:", error);
    }
  };

  // Fetch platform-specific sync history
  const fetchPlatformSyncHistory = async (platform: string) => {
    try {
      const response = await axiosInstance.get(`${SYNC_STATUS_API}/${platform}`);
      if (response.data.status === "success") {
        return response.data.sync_history || [];
      }
      return [];
    } catch (error) {
      console.error(`[${platform} Sync Status] Error fetching sync history:`, error);
      return [];
    }
  };

  // Handle opening sync modal for a specific platform
  const handleViewSyncHistory = async (platform: string) => {
    setSelectedPlatform(platform);
    setShowSyncModal(true);
    const history = await fetchPlatformSyncHistory(platform);
    setPlatformSyncHistory(history);
  };

  // Handle closing sync modal
  const handleCloseSyncModal = () => {
    setShowSyncModal(false);
    setSelectedPlatform(null);
    setPlatformSyncHistory([]);
  };

  // Trigger manual sync for a platform
  const triggerSync = async (platform: string) => {
    setIsSyncing(true);
    try {
      const response = await axiosInstance.post(`${TRIGGER_SYNC_API}/${platform}?days_back=7`);
      if (response.data.status === "success") {
        message.success(`${platform} sync has been triggered. Data will be updated shortly.`);
        // Refresh sync history after a short delay
        setTimeout(() => {
          fetchSyncHistory();
        }, 2000);
      } else {
        message.error(`Failed to trigger ${platform} sync`);
      }
    } catch (error) {
      console.error(`[${platform} Sync] Error:`, error);
      message.error(`Failed to trigger ${platform} sync`);
    } finally {
      setIsSyncing(false);
    }
  };

  useEffect(() => {
    // Fetch sync history when component mounts
    if (status.shopify === "connected") {
      fetchSyncHistory();
    }
  }, [status]);

  // Handle OAuth callback from Shopify app
  useEffect(() => {
    // Check URL query params for Shopify OAuth callback
    const searchParams = new URLSearchParams(window.location.search);

    const shop = searchParams.get("shop");
    const session = searchParams.get("session");
    const hmac = searchParams.get("hmac");

    // New flow: Shopify app redirects back with shop, session, and hmac params
    if (shop && session && hmac) {
      console.log("[Shopify Integration] OAuth success detected:", {
        shop,
        session: session.substring(0, 10) + "...",
      });

      // Clear sessionStorage flag
      sessionStorage.removeItem("shopify_oauth_pending");

      // Clear URL query parameters
      const cleanUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, "", cleanUrl);

      // Show success message
      message.success(`Shopify integration completed successfully! Store: ${shop}`);

      // Refetch integration status from API to get real connection state
      fetchIntegrationStatus();
      return;
    }

    // Legacy flow: check for old callback format
    const oauthCallback = searchParams.get("oauth_callback");
    const installStatus = searchParams.get("install_status");
    const error = searchParams.get("error");

    if (oauthCallback === "shopify") {
      console.log("[Shopify Integration] Legacy OAuth callback detected:", {
        installStatus,
        shop,
        error
      });

      sessionStorage.removeItem("shopify_oauth_pending");
      const cleanUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, "", cleanUrl);

      if (installStatus === "success") {
        message.success(`Shopify integration completed successfully!${shop ? ` Store: ${shop}` : ''}`);
        // Refetch integration status from API
        fetchIntegrationStatus();
      } else if (installStatus === "error") {
        setStatus((prev) => ({ ...prev, shopify: "error" }));
        const errorMessages: Record<string, string> = {
          hmac_verification_failed: "Security verification failed. Please try again.",
          invalid_state: "Invalid session. Please restart the integration.",
          shop_mismatch: "Store domain mismatch. Please try again.",
          token_exchange_failed: "Failed to complete authorization. Please try again.",
          internal_error: "An unexpected error occurred. Please try again."
        };
        const errorMessage = error ? errorMessages[error] || "Integration failed" : "Integration failed";
        message.error(errorMessage);
      }
    } else {
      // No OAuth callback - clear any stale pending state
      sessionStorage.removeItem("shopify_oauth_pending");
    }
  }, []);

  const handleAmazonLogin = async () => {
    setStatus((prev) => ({ ...prev, amazon: "loading" }));

    try {
      // Simulate Amazon MWS/SP-API authentication
      await axiosInstance.post("/api/amazon/auth", {
        tenantId: getTenantId(),
        marketplace_id: "ATVPDKIKX0DER", // US marketplace
        seller_id: "mock_seller_id",
        access_token: "mock_amazon_access_token",
        refresh_token: "mock_amazon_refresh_token",
      });

      setStatus((prev) => ({ ...prev, amazon: "connected" }));
    } catch (error) {
      console.error("Error with Amazon integration", error);
      setStatus((prev) => ({ ...prev, amazon: "error" }));
    }
  };

  const handleShopifyLogin = () => {
    // Show DPA modal first before initiating OAuth
    setShowDPAModal(true);
  };

  const handleDPAAccept = async () => {
    // User accepted DPA, proceed with OAuth
    setDpaAccepted(true);
    setStatus((prev) => ({ ...prev, shopify: "loading" }));

    try {
      // Mark OAuth as pending
      sessionStorage.setItem("shopify_oauth_pending", "true");

      // Store DPA acceptance timestamp
      sessionStorage.setItem("shopify_dpa_accepted", new Date().toISOString());

      console.log("[Shopify Integration] DPA accepted, fetching OAuth URL...");

      // Get OAuth URL from backend
      const response = await axiosInstance.get(SHOPIFY_INTEGRATION_AUTH_API);

      if (response.data.status === "success" && response.data.oauth_url) {
        const oauthUrl = response.data.oauth_url;
        console.log("[Shopify Integration] Redirecting directly to Shopify:", oauthUrl);

        // Close modal and redirect to Shopify OAuth page
        setShowDPAModal(false);

        // Redirect directly to Shopify OAuth page
        // Shopify will ask for shop domain, then redirect back to /shopify/auth/callback
        window.location.href = oauthUrl;
      } else {
        throw new Error("Failed to get OAuth URL from backend");
      }

    } catch (error) {
      console.error("Error initiating Shopify login", error);
      setStatus((prev) => ({ ...prev, shopify: "error" }));
      setShowDPAModal(false);
      message.error("Failed to initiate Shopify integration");
      sessionStorage.removeItem("shopify_oauth_pending");
    }
  };

  const handleDPADecline = () => {
    // User declined DPA
    setShowDPAModal(false);
    setStatus((prev) => ({ ...prev, shopify: "idle" }));
    message.info("You must accept the Data Processing Agreement to connect with Shopify");
  };

  const handleShopifyDisconnect = async () => {
    Modal.confirm({
      title: "Disconnect Shopify Integration",
      content: (
        <div>
          <p>Are you sure you want to disconnect Shopify?</p>
          <p style={{ marginTop: 8, color: '#666', fontSize: '13px' }}>
            This will remove access to your Shopify data. Per Shopify compliance requirements,
            all synced transaction data will be permanently deleted within 30 days of disconnection.
          </p>
        </div>
      ),
      okText: "Disconnect",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          setStatus((prev) => ({ ...prev, shopify: "loading" }));
          console.log("[Shopify Integration] Disconnecting...");

          const response = await axiosInstance.delete("/api/integrations/disconnect/shopify");

          if (response.data.status === "success") {
            setStatus((prev) => ({ ...prev, shopify: "idle" }));
            message.success("Shopify integration disconnected successfully. Synced data will be deleted within 30 days.");
            console.log("[Shopify Integration] Disconnected successfully");
          } else {
            throw new Error("Failed to disconnect");
          }
        } catch (error) {
          console.error("Error disconnecting Shopify", error);
          setStatus((prev) => ({ ...prev, shopify: "error" }));
          message.error("Failed to disconnect Shopify integration");
        }
      },
    });
  };

  const handleWooCommerceLogin = async () => {
    setStatus((prev) => ({ ...prev, woocommerce: "loading" }));
    try {
      // Simulate WooCommerce REST API authentication
      const siteUrl = prompt("Enter your WooCommerce site URL:");
      const consumerKey = prompt("Enter your Consumer Key:");
      const consumerSecret = prompt("Enter your Consumer Secret:");

      if (!siteUrl || !consumerKey || !consumerSecret) {
        setStatus((prev) => ({ ...prev, woocommerce: "error" }));
        return;
      }
      await axiosInstance.post("/api/woocommerce/auth", {
        tenantId: getTenantId(),
        site_url: siteUrl,
        consumer_key: consumerKey,
        consumer_secret: consumerSecret,
      });

      setStatus((prev) => ({ ...prev, woocommerce: "connected" }));
    } catch (error) {
      setStatus((prev) => ({ ...prev, woocommerce: "error" }));
    }
  };

  const handleEtsyLogin = async () => {
    setStatus((prev) => ({ ...prev, etsy: "loading" }));

    try {
      // Simulate Etsy OAuth flow
      await axiosInstance.post("/api/etsy/auth", {
        tenantId: getTenantId(),
        access_token: "mock_etsy_access_token",
        refresh_token: "mock_etsy_refresh_token",
        shop_id: "mock_etsy_shop_id",
      });

      setStatus((prev) => ({ ...prev, etsy: "connected" }));
    } catch (error) {
      console.error("Error with Etsy integration", error);
      setStatus((prev) => ({ ...prev, etsy: "error" }));
    }
  };

  const getStatusIcon = (platformStatus: string) => {
    switch (platformStatus) {
      case "loading":
        return <Loader2 className="w-5 h-5 animate-spin text-blue-500" />;
      case "connected":
        return <Check className="w-5 h-5 text-green-500" />;
      case "error":
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return null;
    }
  };

  const getButtonText = (platform: string, platformStatus: string) => {
    switch (platformStatus) {
      case "loading":
        return "Connecting...";
      case "connected":
        return "Connected";
      case "error":
        return "Retry Connection";
      default:
        return `Connect ${platform}`;
    }
  };

  const getButtonStyle = (platformStatus: string, baseColor: string) => {
    switch (platformStatus) {
      case "connected":
        return "bg-green-500 hover:bg-green-600 cursor-default";
      case "error":
        return "bg-red-500 hover:bg-red-600";
      case "loading":
        return `${baseColor} opacity-75 cursor-not-allowed`;
      default:
        return `${baseColor} hover:opacity-90`;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-cyan-50 p-4 md:p-8">
      {/* DPA Modal */}
      <DPAModal
        isOpen={showDPAModal}
        onAccept={handleDPAAccept}
        onDecline={handleDPADecline}
        loading={status.shopify === "loading"}
      />

      {/* Platform Sync Status Modal */}
      <SyncStatusModal
        isOpen={showSyncModal}
        onClose={handleCloseSyncModal}
        platformName={selectedPlatform ? selectedPlatform.charAt(0).toUpperCase() + selectedPlatform.slice(1) : ''}
        syncHistory={platformSyncHistory}
        isLoading={false}
      />

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            E-commerce Platform Integration
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Connect your online stores to sync sales data, track performance,
            and get unified analytics across all your e-commerce platforms.
          </p>
        </div>

        {/* Sync Status Dashboard - Removed: Now showing platform-specific sync history in modal */}

        {/* Integration Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {/* Shopify Integration */}
          <div className="bg-white rounded-2xl shadow-xl p-8 transform hover:scale-105 transition-all duration-300 border border-gray-100">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-green-50 rounded-full flex items-center justify-center">
                <svg
                  className="w-12 h-12 text-green-600"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M15.337 2.974c-.222 0-.444.066-.621.178-.355.222-.533.622-.533 1.066v.8c-.8-.133-1.644-.133-2.489 0v-.8c0-.444-.178-.844-.533-1.066-.177-.112-.399-.178-.621-.178s-.444.066-.621.178c-.355.222-.533.622-.533 1.066v.8c-.8-.133-1.644-.133-2.489 0v-.8c0-.444-.178-.844-.533-1.066-.177-.112-.399-.178-.621-.178s-.444.066-.621.178c-.355.222-.533.622-.533 1.066v1.6c0 .444.178.844.533 1.066.177.112.399.178.621.178s.444-.066.621-.178c.355-.222.533-.622.533-1.066v-.8c.8.133 1.644.133 2.489 0v.8c0 .444.178.844.533 1.066.177.112.399.178.621.178s.444-.066.621-.178c.355-.222.533-.622.533-1.066v-.8c.8.133 1.644.133 2.489 0v.8c0 .444.178.844.533 1.066.177.112.399.178.621.178s.444-.066.621-.178c.355-.222.533-.622.533-1.066V4.04c0-.444-.178-.844-.533-1.066-.177-.112-.399-.178-.621-.178z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Shopify</h3>
              <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                Integrate your Shopify store to access comprehensive sales data,
                customer insights, and product analytics.
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-gray-900 mb-2">
                  Data Available:
                </h4>
                <ul className="text-xs text-gray-600 space-y-1 text-left">
                  <li>• Real-time sales and revenue data</li>
                  {/* <li>• Customer behavior analytics</li> */}
                  <li>• Product performance tracking</li>
                  {/* <li>• Inventory management data</li> */}
                  <li>• Analytics Data</li>
                </ul>
              </div>
              <div className="flex items-center justify-center mb-4">
                {getStatusIcon(status.shopify)}
                <span className="ml-2 text-sm font-medium text-gray-700">
                  {status.shopify === "connected"
                    ? "Successfully Connected"
                    : status.shopify === "error"
                    ? "Connection Failed"
                    : status.shopify === "loading"
                    ? "Connecting..."
                    : "Not Connected"}
                </span>
              </div>
              {status.shopify === "connected" ? (
                <div className="space-y-2">
                  <button
                    onClick={() => triggerSync('shopify')}
                    disabled={isSyncing}
                    className="w-full py-3 px-6 rounded-xl text-white font-semibold transition-all duration-200 bg-blue-600 hover:bg-blue-700 flex items-center justify-center gap-2"
                  >
                    {isSyncing ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-5 h-5" />
                        Sync Data
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => handleViewSyncHistory('shopify')}
                    className="w-full py-2 px-4 rounded-xl text-indigo-700 font-medium transition-all duration-200 bg-indigo-50 hover:bg-indigo-100 flex items-center justify-center gap-2"
                  >
                    <TrendingUp className="w-4 h-4" />
                    View Sync History
                  </button>
                  <button
                    onClick={handleShopifyDisconnect}
                    className="w-full py-2 px-4 rounded-xl text-gray-700 font-medium transition-all duration-200 bg-gray-100 hover:bg-gray-200"
                  >
                    Disconnect
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleShopifyLogin}
                  disabled={status.shopify === "loading"}
                  className={`w-full py-3 px-6 rounded-xl text-white font-semibold transition-all duration-200 ${getButtonStyle(
                    status.shopify,
                    "bg-green-600 hover:bg-green-700"
                  )}`}
                >
                  {getButtonText("Shopify", status.shopify)}
                </button>
              )}
            </div>
          </div>

          {/* Amazon Integration */}
          <div className="bg-white rounded-2xl shadow-xl p-8 transform hover:scale-105 transition-all duration-300 border border-gray-100">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-orange-50 rounded-full flex items-center justify-center">
                <svg
                  className="w-12 h-12 text-orange-600"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M.045 18.02c.072-.116.187-.124.348-.022 3.636 2.11 7.594 3.166 11.87 3.166 2.643 0 5.353-.508 8.138-1.524 1.578-.578 3.046-1.297 4.403-2.157.1-.063.187-.055.26.022.07.077.07.162 0 .255-.47.534-1.235 1.083-2.292 1.646-1.506.82-3.131 1.458-4.876 1.915-1.746.456-3.56.684-5.442.684-2.28 0-4.438-.39-6.47-1.175-1.194-.455-2.28-1.057-3.26-1.806-.98-.75-1.741-1.53-2.28-2.342-.072-.116-.072-.201 0-.255l.001-.407z" />
                  <path d="M20.076 15.608c-.123 0-.234-.028-.333-.085-.1-.056-.21-.162-.333-.315l-2.375-3.507c-.295-.436-.295-.87 0-1.306.072-.116.173-.211.304-.285.13-.073.27-.11.42-.11.072 0 .161.018.267.055.105.036.189.073.25.11l2.897 1.96c.295.198.442.453.442.765 0 .312-.147.567-.442.765l-2.897 1.96c-.061.037-.145.074-.25.11-.106.037-.195.055-.267.055l-.683-.203z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Amazon Seller Central
              </h3>
              <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                Connect your Amazon Seller account to sync orders, inventory,
                and sales analytics from all Amazon marketplaces.
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-gray-900 mb-2">
                  Data Available:
                </h4>
                <ul className="text-xs text-gray-600 space-y-1 text-left">
                  <li>• Order history and fulfillment status</li>
                  <li>• Product performance metrics</li>
                  <li>• Advertising campaign data</li>
                  <li>• Inventory levels and forecasting</li>
                  <li>• Customer reviews and ratings</li>
                </ul>
              </div>
              <div className="flex items-center justify-center mb-4">
                {getStatusIcon(status.amazon)}
                <span className="ml-2 text-sm font-medium text-gray-700">
                  {status.amazon === "connected"
                    ? "Successfully Connected"
                    : status.amazon === "error"
                    ? "Connection Failed"
                    : status.amazon === "loading"
                    ? "Connecting..."
                    : "Not Connected"}
                </span>
              </div>
              {status.amazon === "connected" ? (
                <div className="space-y-2">
                  <button
                    onClick={() => triggerSync('amazon')}
                    disabled={isSyncing}
                    className="w-full py-3 px-6 rounded-xl text-white font-semibold transition-all duration-200 bg-blue-600 hover:bg-blue-700 flex items-center justify-center gap-2"
                  >
                    {isSyncing ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-5 h-5" />
                        Sync Data
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => handleViewSyncHistory('amazon')}
                    className="w-full py-2 px-4 rounded-xl text-indigo-700 font-medium transition-all duration-200 bg-indigo-50 hover:bg-indigo-100 flex items-center justify-center gap-2"
                  >
                    <TrendingUp className="w-4 h-4" />
                    View Sync History
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleAmazonLogin}
                  disabled={status.amazon === "loading"}
                  className={`w-full py-3 px-6 rounded-xl text-white font-semibold transition-all duration-200 ${getButtonStyle(
                    status.amazon,
                    "bg-orange-600 hover:bg-orange-700"
                  )}`}
                >
                  {getButtonText("Amazon", status.amazon)}
                </button>
              )}
            </div>
          </div>

          {/* WooCommerce Integration */}
          <div className="bg-white rounded-2xl shadow-xl p-8 transform hover:scale-105 transition-all duration-300 border border-gray-100">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-purple-50 rounded-full flex items-center justify-center">
                <svg
                  className="w-12 h-12 text-purple-600"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M22.907 6.84c-.05-1.67-.94-3.145-2.366-3.928C19.47 2.466 18.24 2.25 17.01 2.25H6.99c-1.23 0-2.46.216-3.531.662C2.033 3.695 1.143 5.17 1.093 6.84c-.036 1.215.216 2.43.756 3.537.54 1.107 1.35 2.034 2.34 2.7.99.666 2.16 1.035 3.402 1.071H16.41c1.242-.036 2.412-.405 3.402-1.071.99-.666 1.8-1.593 2.34-2.7.54-1.107.792-2.322.756-3.537z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                WooCommerce
              </h3>
              <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                Connect your WordPress WooCommerce store to synchronize order
                data and track your online business performance.
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-gray-900 mb-2">
                  Data Available:
                </h4>
                <ul className="text-xs text-gray-600 space-y-1 text-left">
                  <li>• Order management and tracking</li>
                  <li>• Product sales analytics</li>
                  <li>• Customer purchase history</li>
                  <li>• Payment gateway insights</li>
                  <li>• Shipping and fulfillment data</li>
                </ul>
              </div>
              <div className="flex items-center justify-center mb-4">
                {getStatusIcon(status.woocommerce)}
                <span className="ml-2 text-sm font-medium text-gray-700">
                  {status.woocommerce === "connected"
                    ? "Successfully Connected"
                    : status.woocommerce === "error"
                    ? "Connection Failed"
                    : status.woocommerce === "loading"
                    ? "Connecting..."
                    : "Not Connected"}
                </span>
              </div>
              {status.woocommerce === "connected" ? (
                <div className="space-y-2">
                  <button
                    onClick={() => triggerSync('woocommerce')}
                    disabled={isSyncing}
                    className="w-full py-3 px-6 rounded-xl text-white font-semibold transition-all duration-200 bg-blue-600 hover:bg-blue-700 flex items-center justify-center gap-2"
                  >
                    {isSyncing ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-5 h-5" />
                        Sync Data
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => handleViewSyncHistory('woocommerce')}
                    className="w-full py-2 px-4 rounded-xl text-indigo-700 font-medium transition-all duration-200 bg-indigo-50 hover:bg-indigo-100 flex items-center justify-center gap-2"
                  >
                    <TrendingUp className="w-4 h-4" />
                    View Sync History
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleWooCommerceLogin}
                  disabled={status.woocommerce === "loading"}
                  className={`w-full py-3 px-6 rounded-xl text-white font-semibold transition-all duration-200 ${getButtonStyle(
                    status.woocommerce,
                    "bg-purple-600 hover:bg-purple-700"
                  )}`}
                >
                  {getButtonText("WooCommerce", status.woocommerce)}
                </button>
              )}
            </div>
          </div>

          {/* Etsy Integration */}
          <div className="bg-white rounded-2xl shadow-xl p-8 transform hover:scale-105 transition-all duration-300 border border-gray-100">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-orange-50 rounded-full flex items-center justify-center">
                <svg
                  className="w-12 h-12 text-orange-500"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M9.16504 11.3793C9.16504 12.2305 9.16504 13.0859 9.16504 13.9434C9.16504 14.2461 9.19727 14.5469 9.24609 14.8438C9.31055 15.2734 9.44531 15.6758 9.69727 16.0234C10.1426 16.6406 10.8281 16.9336 11.6016 16.9336C12.4688 16.9336 13.2402 16.5352 13.6855 15.8477C13.8828 15.5547 14.0156 15.2188 14.0723 14.8633C14.1289 14.5078 14.1289 14.1445 14.1289 13.7812V7.06641H16.3574V13.8594C16.3574 14.3945 16.3398 14.9297 16.2734 15.4629C16.207 15.9961 16.0781 16.5176 15.8789 17.0156C15.4336 18.2383 14.5605 19.2148 13.4297 19.7578C12.7051 20.0996 11.9062 20.2539 11.1055 20.2148C10.3555 20.2148 9.61328 20.0625 8.94727 19.7695C7.91602 19.3242 7.08984 18.5215 6.64453 17.5234C6.42188 17.0156 6.2832 16.4727 6.23438 15.9199C6.18555 15.3672 6.16797 14.8125 6.16797 14.2578V7.06641H8.39648V11.3793H9.16504Z" />
                  <path d="M1.5 7.06641H5.71875V8.67188H3.72852V13.9434H5.71875V15.5488H1.5V7.06641Z" />
                  <path d="M18.6406 15.5488V13.9434H20.6309V8.67188H18.6406V7.06641H22.8594V15.5488H18.6406Z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Etsy</h3>
              <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                Integrate your Etsy shop to track handmade and vintage item
                sales, manage listings, and analyze customer trends.
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-gray-900 mb-2">
                  Data Available:
                </h4>
                <ul className="text-xs text-gray-600 space-y-1 text-left">
                  <li>• Shop performance metrics</li>
                  <li>• Listing views and favorites</li>
                  <li>• Order and transaction data</li>
                  <li>• Customer review analytics</li>
                  <li>• Fee and profit calculations</li>
                </ul>
              </div>
              <div className="flex items-center justify-center mb-4">
                {getStatusIcon(status.etsy)}
                <span className="ml-2 text-sm font-medium text-gray-700">
                  {status.etsy === "connected"
                    ? "Successfully Connected"
                    : status.etsy === "error"
                    ? "Connection Failed"
                    : status.etsy === "loading"
                    ? "Connecting..."
                    : "Not Connected"}
                </span>
              </div>
              {status.etsy === "connected" ? (
                <div className="space-y-2">
                  <button
                    onClick={() => triggerSync('etsy')}
                    disabled={isSyncing}
                    className="w-full py-3 px-6 rounded-xl text-white font-semibold transition-all duration-200 bg-blue-600 hover:bg-blue-700 flex items-center justify-center gap-2"
                  >
                    {isSyncing ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-5 h-5" />
                        Sync Data
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => handleViewSyncHistory('etsy')}
                    className="w-full py-2 px-4 rounded-xl text-indigo-700 font-medium transition-all duration-200 bg-indigo-50 hover:bg-indigo-100 flex items-center justify-center gap-2"
                  >
                    <TrendingUp className="w-4 h-4" />
                    View Sync History
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleEtsyLogin}
                  disabled={status.etsy === "loading"}
                  className={`w-full py-3 px-6 rounded-xl text-white font-semibold transition-all duration-200 ${getButtonStyle(
                    status.etsy,
                    "bg-orange-500 hover:bg-orange-600"
                  )}`}
                >
                  {getButtonText("Etsy", status.etsy)}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Features Section */}
        {/* <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">
            Unified E-commerce Analytics
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                <TrendingUp className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Multi-Platform Analytics</h3>
              <p className="text-gray-600">Get comprehensive sales analytics across all your e-commerce platforms in a single dashboard.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                <Package className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Inventory Management</h3>
              <p className="text-gray-600">Sync inventory levels across platforms and get alerts for low stock or out-of-stock items.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-purple-100 rounded-full flex items-center justify-center">
                <DollarSign className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Revenue Optimization</h3>
              <p className="text-gray-600">Identify top-performing products, optimize pricing strategies, and maximize your profit margins.</p>
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default EcommerceIntegration;
